import React, { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import AuthButton from './AuthButton'
import { useSupabaseAuth } from '../hooks/useSupabaseAuth.js'

export default function Header() {
  const { isSignedIn, signOut, isAdmin } = useSupabaseAuth()
  const [open, setOpen] = useState(false)
  const [shrink, setShrink] = useState(false)
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onScroll() {
      setShrink(window.scrollY > 12)
    }
    onScroll()
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    // Setup install listener
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true)
        setIsInstallable(false)
        return
      }
      
      // Check for iOS standalone mode
      if ('standalone' in window.navigator && (window.navigator as any).standalone === true) {
        setIsInstalled(true)
        setIsInstallable(false)
        return
      }
    }

    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setIsInstallable(true)
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      setIsInstallable(false)
      setDeferredPrompt(null)
    }

    checkIfInstalled()
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      if (navigator.userAgent.includes('Chrome')) {
        alert('To install the Park Model Prices app:\n\n1. Click the menu (⋮) in your browser\n2. Select "Install Park Model Prices"\n3. Click "Install"')
      } else {
        alert('To install the Park Model Prices app, look for the install option in your browser menu.')
      }
      return
    }

    try {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        setIsInstalled(true)
        setIsInstallable(false)
      }
      
      setDeferredPrompt(null)
    } catch (error) {
      console.error('Error during installation:', error)
    }
  }

  const handleAboutMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
    }
    setAboutDropdownOpen(true)
  }

  const handleAboutMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setAboutDropdownOpen(false)
    }, 300) // 300ms delay before closing
  }

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
    }
  }

  const handleDropdownMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setAboutDropdownOpen(false)
    }, 300) // 300ms delay before closing
  }

  function NavLinks() {
    return (
      <nav className="hidden md:flex items-center gap-6 text-sm">
        <Link to="/models" className={`hover:text-yellow-400 ${shrink ? 'text-gray-700' : 'text-gray-300'}`}>Explore Models</Link>
        {isSignedIn && (
          <Link to="/builds" className={`hover:text-yellow-400 ${shrink ? 'text-gray-700' : 'text-gray-300'}`}>My Home</Link>
        )}
        <Link to="/financing" className={`hover:text-yellow-400 ${shrink ? 'text-gray-700' : 'text-gray-300'}`}>Financing</Link>
        <Link to="/how" className={`hover:text-yellow-400 ${shrink ? 'text-gray-700' : 'text-gray-300'}`}>How It Works</Link>
        <Link to="/park-model-resorts" className={`hover:text-yellow-400 ${shrink ? 'text-gray-700' : 'text-gray-300'}`}>Park Model Resorts</Link>
        <Link to="/blog" className={`hover:text-yellow-400 ${shrink ? 'text-gray-700' : 'text-gray-300'}`}>Blog</Link>
        <div 
          className="relative"
          onMouseEnter={handleAboutMouseEnter}
          onMouseLeave={handleAboutMouseLeave}
        >
          <button className={`hover:text-yellow-400 flex items-center gap-1 ${shrink ? 'text-gray-700' : 'text-gray-300'}`}>
            About
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {aboutDropdownOpen && (
            <div 
              ref={dropdownRef}
              className="absolute top-full left-0 mt-1 w-48 bg-gray-900 border border-gray-800 rounded-lg shadow-lg z-50"
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            >
              <Link to="/faq" className="block px-4 py-2 text-sm text-gray-300 hover:text-yellow-400 hover:bg-gray-800">FAQ</Link>
              <Link to="/about" className="block px-4 py-2 text-sm text-gray-300 hover:text-yellow-400 hover:bg-gray-800">About Us</Link>
              <Link to="/about/team" className="block px-4 py-2 text-sm text-gray-300 hover:text-yellow-400 hover:bg-gray-800">Our Team</Link>
              <Link to="/about/manufacturer" className="block px-4 py-2 text-sm text-gray-300 hover:text-yellow-400 hover:bg-gray-800">About the Manufacturer</Link>
              {!isInstalled && (isInstallable || navigator.userAgent.includes('Chrome')) && (
                <button
                  onClick={handleInstallClick}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:text-yellow-400 hover:bg-gray-800"
                >
                  Install App
                </button>
              )}
            </div>
          )}
        </div>
      </nav>
    )
  }

  return (
    <header className={`sticky top-0 z-40 transition-all ${shrink ? 'py-3 bg-gray-100/95 border-b border-gray-300 backdrop-blur-sm' : 'py-5 bg-gray-900/60 border-b border-gray-800 backdrop-blur-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <div className="flex items-center">
          <Link to="/" className="flex items-center">
            <div className="overflow-hidden rounded-md">
              <img src="/logo/firefly-logo.png" alt="Park Model Prices by Firefly Tiny Homes" className="h-10 w-auto" loading="eager" />
            </div>
          </Link>
        </div>

        <NavLinks />

        <div className="flex items-center gap-3">
          {isAdmin && (
            <Link to="/admin" className="hidden sm:inline text-sm px-3 py-1.5 rounded bg-yellow-500 text-gray-900 hover:bg-yellow-400">Admin</Link>
          )}
          <AuthButton />
          <button aria-label="Open menu" className="md:hidden p-2 rounded hover:bg-white/10" onClick={() => setOpen(true)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={shrink ? "text-gray-900" : "text-gray-100"}>
              <path d="M3 8h18M3 12h18M3 16h18" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)}></div>
          <div className="absolute right-0 top-0 h-full w-80 max-w-[80%] bg-white border-l border-gray-200 p-4 flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="overflow-hidden rounded-md">
                  <img src="/logo/firefly-logo.png" alt="Park Model Prices by Firefly Tiny Homes" className="h-8 w-auto" />
                </div>
              </div>
              <button aria-label="Close menu" onClick={() => setOpen(false)} className="p-2 rounded hover:bg-gray-100">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-900"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            
            {/* Main Navigation */}
            <nav className="flex flex-col gap-3 text-sm flex-1">
              <Link to="/models" className="px-2 py-2 rounded hover:bg-gray-100 text-gray-900 font-semibold" onClick={() => setOpen(false)}>Explore Models</Link>
              {isSignedIn && (
                <Link to="/builds" className="px-2 py-2 rounded hover:bg-gray-100 text-gray-900 font-semibold" onClick={() => setOpen(false)}>My Home</Link>
              )}
              <Link to="/financing" className="px-2 py-2 rounded hover:bg-gray-100 text-gray-900 font-semibold" onClick={() => setOpen(false)}>Financing</Link>
              <Link to="/how" className="px-2 py-2 rounded hover:bg-gray-100 text-gray-900 font-semibold" onClick={() => setOpen(false)}>How It Works</Link>
              <Link to="/park-model-resorts" className="px-2 py-2 rounded hover:bg-gray-100 text-gray-900 font-semibold" onClick={() => setOpen(false)}>Park Model Resorts</Link>
              <Link to="/blog" className="px-2 py-2 rounded hover:bg-gray-100 text-gray-900 font-semibold" onClick={() => setOpen(false)}>Blog</Link>
              <Link to="/faq" className="px-2 py-2 rounded hover:bg-gray-100 text-gray-900 font-semibold" onClick={() => setOpen(false)}>FAQ</Link>
              <Link to="/about" className="px-2 py-2 rounded hover:bg-gray-100 text-gray-900 font-semibold" onClick={() => setOpen(false)}>About</Link>
              {isAdmin && (
                <Link to="/admin" className="px-2 py-2 rounded bg-yellow-500 text-gray-900 hover:bg-yellow-400 font-semibold" onClick={() => setOpen(false)}>Admin</Link>
              )}
            </nav>
            
            {/* Sign Out at Bottom */}
            {isSignedIn && (
              <div className="border-t border-gray-200 pt-4 mt-auto">
                <button 
                  onClick={() => {
                    signOut()
                    setOpen(false)
                  }}
                  className="flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-100 text-gray-900 font-semibold"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  )
}


